import React from 'react'
import {
  DialogTitle,
  DialogContent,
  Container,
  Row,
  Col,
  TextField,
  Typography,
  Paper,
  DialogActions,
  Button,
  ButtonBase,
  Box,
  Divider,
  IconButton,
  LoadingButton,
  Tooltip,
} from '@applift/factor'
import { Duplicate, InfoCircle } from '@applift/icons'

import { MAX_NAME_CHARS, URL_REGEX } from '../../constants/constants'
import { useMeasurementPartnersList } from '../../hooks/usePostback'
import { copyText } from '../../utils'
import { PostbackCreationInfo } from '../../models/Postback'

import styles from './CreateOrEditPostback.module.scss'

export interface PostbackDetailsProps {
  onClose: () => void
  type: 'create' | 'edit'
  postbackInfo: PostbackCreationInfo
  setPostbackInfo: React.Dispatch<React.SetStateAction<PostbackCreationInfo>>
  onCompletion: (data: PostbackCreationInfo) => void
  isCompletionInProgress?: boolean
}

export const PostbackDetails = (props: PostbackDetailsProps) => {
  const {
    onClose,
    postbackInfo,
    setPostbackInfo,
    type,
    onCompletion,
    isCompletionInProgress,
  } = props

  const isNameEditedRef = React.useRef(false)
  const errorRefs = React.useRef<string[]>([])

  const isCreateFlow = type === 'create'

  const partners = useMeasurementPartnersList()

  const selectedPartnerInEdit = partners?.find(
    partner => postbackInfo.partnerTypeId == partner.id.toString()
  )

  const allowClickUrlCopy =
    postbackInfo.clickUrl && !errorRefs.current.includes('clickURL')

  const allowImpressionUrlCopy =
    postbackInfo.impressionUrl && !errorRefs.current.includes('impressionURL')

  return (
    <>
      <DialogTitle onClose={onClose}>Postback Details</DialogTitle>
      <DialogContent dividers>
        <Container
          sx={{
            display: 'flex',
            flexDirection: 'column',
            bgColor: 'neutral-0',
          }}
        >
          <Row xs={{ col: 2 }} sx={{ mt: 8, alignItems: 'end' }}>
            <Col>
              <TextField
                value={postbackInfo.name}
                onChange={e => {
                  isNameEditedRef.current = true
                  errorRefs.current =
                    e.target.value.trim().length > MAX_NAME_CHARS
                      ? [...errorRefs.current, 'name']
                      : errorRefs.current.filter(field => field !== 'name')
                  setPostbackInfo(prevState => ({
                    ...prevState,
                    name: e.target.value.trim() ? e.target.value : '',
                  }))
                }}
                label={
                  <Box sx={{ display: 'flex', justifyContent: 'between' }}>
                    <Typography variant="label">Postback Name</Typography>
                    <Typography variant="label">
                      {`${postbackInfo.name.length}/${MAX_NAME_CHARS}`}
                    </Typography>
                  </Box>
                }
                placeholder="Enter postback name"
                variant="outlinedDash"
                fullWidth
                helperText={
                  errorRefs.current.includes('name') ? (
                    <Typography variant="label" color="error">
                      Character limit exceeded
                    </Typography>
                  ) : null
                }
                error={errorRefs.current.includes('name')}
              />
            </Col>
            {!isCreateFlow && (
              <Col sx={{ width: 100, paddingLeft: 40 }}>
                <Paper
                  elevation={1}
                  className={styles.attributionPartnerWrapper}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: 2,
                    paddingX: 16,
                    paddingY: 8,
                  }}
                >
                  <Typography
                    sx={{
                      textWeight: 'demi',
                      textColor: 'neutral-500',
                      mr: 16,
                    }}
                  >
                    Attribution Measurement Partner:{' '}
                  </Typography>
                  <img
                    src={selectedPartnerInEdit?.logoUrl}
                    alt="partnerlogo"
                    className={styles.logoImage}
                  />
                  <Typography
                    sx={{
                      textWeight: 'demi',
                      textColor: 'neutral-1000',
                      ml: 8,
                    }}
                  >
                    {selectedPartnerInEdit?.name}
                  </Typography>
                </Paper>
              </Col>
            )}
          </Row>
          <Divider sx={{ my: 32 }} />
          {isCreateFlow && (
            <>
              <Typography>Select an attribution measurement partner</Typography>
              <Row sx={{ marginTop: 16 }} xs={{ gutterSize: 16 }}>
                {partners?.map(partner => {
                  const isSelectedPartner =
                    postbackInfo.partnerTypeId == partner.id.toString()
                  return (
                    <Col sx={{ marginTop: 0 }} key={partner.name}>
                      <ButtonBase
                        onClick={() =>
                          setPostbackInfo(prevState => ({
                            ...prevState,
                            partnerTypeId: partner.id.toString(),
                          }))
                        }
                      >
                        <Paper
                          elevation={2}
                          style={{ width: '212px' }}
                          sx={{
                            borderRadius: 4,
                            py: 16,
                            mx: 'auto',
                            border: 1,
                            borderColor: isSelectedPartner
                              ? 'primary-600'
                              : 'neutral-0',
                            bgColor: isSelectedPartner
                              ? 'primary-50'
                              : 'neutral-0',
                          }}
                          className={styles.partnerOption}
                        >
                          <Box sx={{ mx: 'auto' }}>
                            <img
                              src={partner.logoUrl}
                              alt={partner.name}
                              style={{
                                height: '48px',
                                width: '48px',
                                objectFit: 'contain',
                              }}
                            />
                          </Box>
                          <Typography
                            component="h5"
                            variant="bodySmall"
                            weight="demi"
                          >
                            {partner.name}
                          </Typography>
                        </Paper>
                      </ButtonBase>
                    </Col>
                  )
                })}
              </Row>
            </>
          )}
          <Row sx={{ mt: isCreateFlow ? 40 : 0 }}>
            <Col sx={{ display: 'flex' }}>
              <TextField
                value={postbackInfo.impressionUrl}
                multiline
                inputProps={{
                  style: { resize: 'none' },
                }}
                rows={5}
                disabled={!isCreateFlow}
                onChange={e => {
                  errorRefs.current = !URL_REGEX.test(e.target.value.trim())
                    ? [...errorRefs.current, 'impressionURL']
                    : errorRefs.current.filter(
                        field => field !== 'impressionURL'
                      )
                  setPostbackInfo(prevState => ({
                    ...prevState,
                    impressionUrl: e.target.value.trim() ? e.target.value : '',
                  }))
                }}
                label={
                  <Tooltip
                    title="This tracking URL will be automatically appended to all creatives to monitor ad impressions on users' devices."
                    placement="bottom-start"
                    arrow
                  >
                    <Box
                      Component={'span'}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 2,
                      }}
                      style={{ pointerEvents: 'all' }}
                    >
                      <Typography variant="label">
                        Impression Pixel URL
                      </Typography>
                      <InfoCircle fontSize={14} />
                    </Box>
                  </Tooltip>
                }
                placeholder="https://example.com"
                variant="outlinedDash"
                fullWidth
                helperText={
                  errorRefs.current.includes('impressionURL') ? (
                    <Typography variant="label" color="error">
                      Please enter a valid URL. (e.g. https://example.com)
                    </Typography>
                  ) : null
                }
                error={errorRefs.current.includes('impressionURL')}
              />
              <IconButton
                sx={{
                  mt: 16,
                  ml: 4,
                  alignItems: 'start',
                  height: 25,
                  visibility: allowImpressionUrlCopy ? 'visible' : 'hidden',
                }}
                disableRipple
                onClick={() =>
                  copyText(
                    postbackInfo.impressionUrl,
                    'Impression URL copied successfully'
                  )
                }
                disabled={!allowImpressionUrlCopy}
              >
                <Duplicate fontSize={24} sx={{ textColor: 'neutral-400' }} />
              </IconButton>
            </Col>
            <Col sx={{ display: 'flex' }}>
              <TextField
                value={postbackInfo.clickUrl}
                disabled={!isCreateFlow}
                multiline
                inputProps={{
                  style: { resize: 'none' },
                }}
                rows={5}
                onChange={e => {
                  errorRefs.current = !URL_REGEX.test(e.target.value.trim())
                    ? [...errorRefs.current, 'clickURL']
                    : errorRefs.current.filter(field => field !== 'clickURL')
                  setPostbackInfo(prevState => ({
                    ...prevState,
                    clickUrl: e.target.value.trim() ? e.target.value : '',
                  }))
                }}
                label={
                  <Tooltip
                    title="When users click on the ad, they will be directed to the destination specified in the click URL. This URL takes precedence over the Creative Click URL"
                    placement="bottom-start"
                    arrow
                  >
                    <Box
                      Component={'span'}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 2,
                      }}
                      style={{ pointerEvents: 'all' }}
                    >
                      <Typography variant="label">Click URL</Typography>
                      <InfoCircle fontSize={14} />
                    </Box>
                  </Tooltip>
                }
                placeholder="https://example.com"
                variant="outlinedDash"
                fullWidth
                helperText={
                  errorRefs.current.includes('clickURL') ? (
                    <Typography variant="label" color="error">
                      Please enter a valid URL. (e.g. https://example.com)
                    </Typography>
                  ) : null
                }
                error={errorRefs.current.includes('clickURL')}
              />
              <IconButton
                sx={{
                  mt: 16,
                  ml: 4,
                  alignItems: 'start',
                  bgColor: 'neutral-0',
                  height: 25,
                  visibility: allowClickUrlCopy ? 'visible' : 'hidden',
                }}
                disableRipple
                disabled={!allowClickUrlCopy}
                onClick={() =>
                  copyText(
                    postbackInfo.clickUrl,
                    'Click URL copied successfully'
                  )
                }
              >
                <Duplicate fontSize={24} sx={{ textColor: 'neutral-400' }} />
              </IconButton>
            </Col>
          </Row>
        </Container>
      </DialogContent>

      <DialogActions>
        <Button color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          disabled={
            !(
              Object.values(postbackInfo).every(value => Boolean(value)) &&
              !errorRefs.current.length
            ) ||
            (!isCreateFlow && !isNameEditedRef.current)
          }
          onClick={() => onCompletion(postbackInfo)}
          loading={isCompletionInProgress}
          variant="contained"
        >
          {isCreateFlow ? 'Create' : 'Save'}
        </LoadingButton>
      </DialogActions>
    </>
  )
}
